// ============================================================
// ⚔️ 战斗系统 — 从 dnf.html 提取
// ============================================================
// 本模块包含所有核心战斗逻辑函数。
// 通过 main.js 暴露到 window.* 供内联代码调用。
// 模块内函数间互相引用直接调用，无需 window. 前缀。
// ============================================================

import { CLASSES } from '../data/classes.js';
import { SKILLS_DB } from '../data/skills.js';
import { S, save, renderTopbar, renderStatusbar } from '../state.js';
import { toast, getEquipStats, isWeaponMismatch, rollEquipmentDrop,
         getAtkEffectDesc, hitStop, showDmgNumber } from '../utils.js';
import { getMonsterData, getMonsterImage, showBattleReveal, showBossClue } from '../data/monsters.js';
import { switchBGM, sfxMonsterRoar, sfxMonsterDeath, sfxHeavyHit, sfxHit,
         playClassAtkSound, resumeSceneBGM, sfxLevelUp } from '../audio.js';
import { CONSUMABLES, getEquipById } from '../data/equipment.js';

// ── 战斗入口 ──
export function startCombat(monsterId, parentScene) {
  if(!S.mp && S.mp !== 0) S.mp = 50;
  const monBase = getMonsterData(monsterId);
  const mon = { ...monBase }; // clone

  // 恶魔难度加成
  if(S._hellMode) {
    mon.hp = Math.floor(mon.hp * 1.3);
    mon.atk = Math.floor(mon.atk * 1.3);
  }

  // ── Boss多阶段数据硬编码 (防缓存) ──
  const BOSS_PHASE = {
    flame_boss:    { multiPhase:true, phase2Hp:200, phase2AtkBonus:15 },
    bone_dragon:   { multiPhase:true, phase2Hp:250, phase2AtkBonus:20 },
    golem_guard:   { multiPhase:true, phase2Hp:250, phase2AtkBonus:12 },
    ice_witch:     { multiPhase:true, phase2Hp:180, phase2AtkBonus:18 },
    cloud_gargoyle:{ multiPhase:true, phase2Hp:100, phase2AtkBonus:12 },
    sky_dragon:    { multiPhase:true, phase2Hp:400, phase2AtkBonus:25 },
    spider_queen:  { multiPhase:true, phase2Hp:180, phase2AtkBonus:16 },
    arad_demon:    { multiPhase:true, phase2Hp:800, phase2AtkBonus:20 },
    shadow_lord:   { multiPhase:true, phase2Hp:400, phase2AtkBonus:15 },
  };
  const bp = BOSS_PHASE[monsterId];
  if(bp) { mon.multiPhase = bp.multiPhase; mon.phase2Hp = bp.phase2Hp; mon.phase2AtkBonus = bp.phase2AtkBonus; }

  S.combat = {
    monsterId,
    monHp: mon.hp, monMaxHp: mon.hp,
    monAtk: mon.atk, monDef: mon.def,
    monName: mon.name, monColor: mon.color,
    monGold: mon.gold, monExp: mon.exp,
    monLv: mon.lv,
    monDrop: null,
    monStatusAtk: mon.statusAtk || null,
    isBoss: mon.isBoss || false,
    multiPhase: mon.multiPhase || false,
    phase2Hp: mon.phase2Hp || 0,
    phase2AtkBonus: mon.phase2AtkBonus || 0,
    phase2Triggered: false,
    turn: 0,
    log: [],
    parentScene,
    stunTurns: 0,
    weakened: false,
    _baseMonDef: mon.def,
    _debuffs: null,
  };
  // 清理上一场战斗的技能状态
  S._shieldTurns = 0;
  S._invincTurns = 0;
  S._summonData = null;
  S._burnTurns = 0;
  S._burnDmg = 0;
  S._hotTurns = 0;
  S._hotHeal = 0;
  S._activeBuffs = [];
  sfxMonsterRoar();
  window.renderCombat();
}

// ── 有效攻击力 ── (委托给 Character 实例方法)
export function getEffAtk(character) {
  const ch = character || S;
  if (typeof ch.getEffAtk === 'function') return ch.getEffAtk();
  // Fallback for non-Character objects
  const stats = getEquipStats();
  let atk = S.atk + stats.bonusAtk + stats.bonusStr;
  if(S.cls === 'berserk' && S.hp < S.maxHp * 0.5) atk = Math.floor(atk * 1.4);
  if(S.statusEffects && S.statusEffects.curse) atk = Math.floor(atk * 0.5);
  // 混沌被动
  if(S.cls === 'monk' && S.passiveProc) { atk = Math.floor(atk * 1.5); S.passiveProc = false; }
  // 动态Buff
  if(S._activeBuffs && S._activeBuffs.length > 0) {
    S._activeBuffs.forEach(b => { if(b.stat === 'atk') atk += b.val; });
  }
  // 词条加成 (affix system)
  if(typeof window.getAffixEffects === 'function') {
    const aff = window.getAffixEffects();
    if(aff.atk) atk += aff.atk;
    if(aff.atkPct) atk = Math.floor(atk * (1 + aff.atkPct));
  }
  return atk;
}

// ── 有效防御力 ──
export function getEffDef(character) {
  const ch = character || S;
  if (typeof ch.getEffDef === 'function') return ch.getEffDef();
  const stats = getEquipStats();
  let def = S.def + stats.bonusDef;
  if(S._activeBuffs && S._activeBuffs.length > 0) {
    S._activeBuffs.forEach(b => { if(b.stat === 'def') def += b.val; });
  }
  return def;
}

// ── 有效速度 ──
export function getEffSpd() {
  const stats = getEquipStats();
  return S.spd + stats.bonusSpd;
}

// ── Boss减伤被动处理 ──
function applyBossDR(dmg, c) {
  if(!c.isBoss) return dmg;
  const monData = window.getMonsterData ? window.getMonsterData(c.monsterId) : null;
  if(!monData || !pass) return dmg;
  
  // 不死亡骨 — 固定减伤
  if(pass.dmgReduce) {
    dmg = Math.floor(dmg * (1 - pass.dmgReduce));
  }
  // 岩石铠甲 — 概率格挡
  if(pass.blockChance && Math.random() < pass.blockChance) {
    dmg = Math.floor(dmg * (1 - pass.blockAmount));
    c.log.push('🪨 岩石铠甲格挡了部分伤害！');
  }
  // 冰霜护盾
  if(c._iceShield) {
    c._iceShield = false;
    c.log.push('❄️ 冰霜护盾完全格挡！');
    return 0;
  }
  return dmg;
}

// ── 史诗装备战斗被动 (攻击时自动触发) ──
function processEpicPassive(dmg, c) {
  if(!c) return;
  const equippedWeapon = S.equipment && S.equipment.weapon;
  const wp = equippedWeapon ? (typeof window.getEquipById === 'function' ? window.getEquipById(equippedWeapon) : null) : null;
  if(!wp || !wp.atkEffect) return;
  
  const eff = wp.atkEffect;
  let bonusDmg = 0, log = '';
  
  switch(eff) {
    case 'flyingBlade':
      bonusDmg = Math.floor(getEffAtk() * 0.3);
      log = `🗡️ 【${wp.name}】剑气追击 ${bonusDmg} 伤害`;
      burstParticles(document.getElementById('monsterWrap'), '#88ccff', 5, 3);
      break;
    case 'chainLightning':
      bonusDmg = Math.floor(Math.max(1, dmg) * 0.5);
      log = `⚡ 【${wp.name}】闪电链溅射 ${bonusDmg} 伤害`;
      burstParticles(document.getElementById('monsterWrap'), '#ffaa33', 8, 5);
      break;
    case 'frostTouch':
      if(Math.random() < 0.2) { c.stunTurns = (c.stunTurns||0) + 1; log = `❄️ 【${wp.name}】冰封触发！敌人冻结1回合`; }
      break;
    case 'vampirism':
      bonusDmg = Math.floor(Math.max(1, dmg) * 0.18);
      S.hp = Math.min(S.maxHp, S.hp + bonusDmg);
      log = `🩸 【${wp.name}】生命吸取 +${bonusDmg} HP`;
      break;
    case 'infernalSteps':
      if(Math.random() < 0.15) {
        if(!S._burnTurns) { S._burnTurns = 0; S._burnDmg = 0; }
        S._burnDmg = Math.floor(getEffAtk() * 0.2); S._burnTurns = 3;
        log = `🔥 【${wp.name}】炼狱灼烧(${S._burnDmg}/回合×3)`;
      }
      break;
    case 'lifeSteal':
      bonusDmg = Math.floor(Math.max(1, dmg) * 0.12);
      S.hp = Math.min(S.maxHp, S.hp + bonusDmg);
      log = `🩸 【${wp.name}】饮血吸收 +${bonusDmg} HP`;
      break;
    case 'shadowStrike':
      if(Math.random() < 0.1) { bonusDmg = Math.floor(getEffAtk() * 0.4); log = `🌑 【${wp.name}】暗影收割 ${bonusDmg} 额外伤害`; }
      break;
  }
  if(bonusDmg > 0) c.monHp = Math.max(0, c.monHp - bonusDmg);
  if(log) c.log.push(log);
}

// ── 普通攻击 ──
export function combatAttack(character) {
  const ch = character || S;
  const c = S.combat;
  if(c.stunTurns > 0) { c.stunTurns--; c.log.push('你被冰冻，无法行动！'); window.renderCombat(); return; }

  let dmg = Math.max(1, (ch.getEffAtk ? ch.getEffAtk() : getEffAtk()) - c.monDef + Math.floor(Math.random()*8) - 2);

  if(ch.cls === 'sword' && Math.random() < 0.2) {
    dmg *= 2;
    c.log.push(`🔗 连击之心触发！`);
  }
  if(ch.cls === 'ranger' && c.turn === 0) {
    dmg = Math.floor(dmg * 2);
    c.log.push(`🎯 鹰眼一击！`);
  }

  // === 武器特效 ===
  let weaponEffectText = '';
  if(S.equipment && S.equipment.weapon) {
    const weapon = getEquipById(S.equipment.weapon);
    if(weapon && weapon.atkEffect) {
      if(weapon.atkEffect === 'holyLight' && c.isBoss) {
        dmg = Math.floor(dmg * 1.2);
        weaponEffectText = '✨ 圣光裁决！对BOSS伤害+20%';
      } else if(weapon.atkEffect === 'chaosBlade' && Math.random() < 0.05) {
        dmg *= 2;
        weaponEffectText = '🌑 混沌之力涌动！双倍伤害！';
      } else if(weapon.atkEffect === 'darkGrail' && Math.random() < 0.12) {
        dmg += 15;
        weaponEffectText = '🖤 暗影侵蚀！额外15点暗属性伤害';
      } else if(weapon.atkEffect === 'truthLight' && c.isBoss) {
        dmg = Math.floor(dmg * 1.25);
        weaponEffectText = '💡 真理之光照耀！对BOSS伤害+25%';
      }
    }
    if(weapon && weapon.onAttack && Math.random() < 0.35) {
      if(!weaponEffectText) weaponEffectText = `💠 【${weapon.name}】${weapon.onAttack}之力迸发！`;
    }
  }
  if(weaponEffectText) c.log.push(weaponEffectText);

  // Show projectile first
  combatShoot(false);
  setTimeout(() => {
    // 精英词缀：幻影闪避
    if(c._eliteModifier && c._eliteModifier.evade && Math.random() < c._eliteModifier.evade) {
      c.log.push(`💫 ${c.monName}身形一闪，完美闪避了你的攻击！`);
      c.turn++;
      animateMonsterHit(0, false, false);
      setTimeout(()=>monsterRetaliate(), 500);
      return;
    }
    
    dmg = applyBossDR(dmg, c); processEpicPassive(dmg, c);
    c.monHp = Math.max(0, c.monHp - dmg);
    c.turn++;
    animateMonsterHit(dmg);

    if(c.monHp <= 0) { setTimeout(()=>combatWin(), 400); return; }
    setTimeout(()=>monsterRetaliate(), 500);
  }, 320);
}

// ── 技能攻击 ──
export function combatSkill(skillId, character) {
  const ch = character || S;
  if(!skillId) {
    // 兼容旧调用：用第一个技能
    const avail = window.getAvailableSkills();
    const firstReady = avail.find(s => s.ready);
    if(firstReady) skillId = firstReady.id;
    else { toast('没有可用技能！', 1000); return; }
  }
  const c = S.combat;
  if(!c) return;
  
  const sk = (typeof ch.getSkillData === 'function') ? ch.getSkillData(skillId) : window.getSkillData(skillId);
  if(!sk) { toast('技能不存在', 1000); return; }
  
  if(!ch._skillCooldowns) ch._skillCooldowns = {};
  if(!ch._skillUpgrades) ch._skillUpgrades = {};
  
  // 检查解锁
  if(ch.level < sk.unlockLv) { toast(`Lv.${sk.unlockLv}解锁`, 1000); return; }
  
  // 检查冷却
  const cd = ch._skillCooldowns[skillId] || 0;
  if(cd > 0) { toast(`冷却中，${cd}回合后可用`, 1000); return; }
  
  // 检查MP
  if((ch.mp || 0) < sk.cost) { toast(`技力不足！需${sk.cost}`, 1000); return; }
  
  // 检查晕眩/冰冻
  if(c.stunTurns > 0) { c.stunTurns--; c.log.push('你被冰冻，无法行动！'); window.renderCombat(); return; }
  
  // 消耗MP
  if (typeof ch.consumeMp === 'function') { ch.consumeMp(sk.cost); }
  else { ch.mp = Math.max(0, (ch.mp || 0) - sk.cost); }
  
  // 设置冷却
  ch._skillCooldowns[skillId] = sk.cd;
  
  // 记录上次技能
  ch._lastSkillId = skillId;
  ch._lastSkillData = sk;
  S._lastSkillId = skillId;
  S._lastSkillData = sk;

  // 觉醒 Cut-in
  if(sk.type === 'awaken') {
    const colorMap = { sword:'#88ccff', berserk:'#ff5544', ghost:'#aa55ff', asura:'#ffaa33', ranger:'#ffdd44', launcher:'#ff8833', crusader:'#ffddaa', monk:'#cc55ff' };
    const cls = ch.cls || 'sword';
    window.showAwakenCutIn(cls, sk.name, colorMap[cls]||'#ffaa33');
    // Let cut-in play before damage
    setTimeout(() => applySkillDamage(), 1200);
    return;
  } else {
    applySkillDamage();
  }
  
  function applySkillDamage() {
  
  // === 伤害计算 ===
  const effAtk = (typeof ch.getEffAtk === 'function') ? ch.getEffAtk() : getEffAtk();
  let totalDmg = 0;
  let hits = 1;
  
  if(sk.dmg.hits) {
    if(Array.isArray(sk.dmg.hits)) {
      hits = sk.dmg.hits[0] + Math.floor(Math.random() * (sk.dmg.hits[1] - sk.dmg.hits[0] + 1));
    } else {
      hits = sk.dmg.hits;
    }
  }
  
  for(let i = 0; i < hits; i++) {
    let hitDmg = Math.floor(effAtk * sk.dmg.mult);
    if(sk.dmg.ignoreDef) {
      // 完全无视防御
    } else if(sk.dmg.ignoreDefPct) {
      hitDmg = Math.max(2, hitDmg - Math.floor(c.monDef * (1 - sk.dmg.ignoreDefPct)));
    } else {
      hitDmg = Math.max(2, hitDmg - c.monDef);
    }
    
    // HP缩放
    if(sk.dmg.hpScaling) {
      const hpLost = 1 - (ch.hp / ch.maxHp);
      const stages = Math.floor(hpLost / sk.dmg.hpScaling.perPct);
      let bonusMult = Math.min(stages * sk.dmg.hpScaling.bonusPerPct, sk.dmg.hpScaling.maxMult - sk.dmg.mult);
      hitDmg = Math.floor(hitDmg * (1 + bonusMult));
    }
    totalDmg += Math.max(2, hitDmg);
  }
  
  // INT加成
  const intMult = typeof getIntMultiplier === 'function' ? window.getIntMultiplier() : 0;
  if(intMult > 0) totalDmg = Math.floor(totalDmg * (1 + intMult));
  
  // 暴击
  const critBonus = sk.dmg.critBonus || 0;
  const crit = Math.random() < (0.25 + critBonus) || critBonus >= 1.0;
  let critM = 2;
  if(sk.dmg.critMult) critM = sk.dmg.critMult;
  if(crit) { totalDmg = Math.floor(totalDmg * critM); c.st = critBonus >= 1.0 ? '必暴！' : '暴击！'; } else c.st = '';
  
  // 旧版被动兼容
  if(ch.cls === 'ghost' && Math.random() < 0.35 && sk.type !== 'ultimate') {
    totalDmg *= 3;
    c.log.push('👻 鬼神之力爆发！三倍伤害！');
  }
  if(ch.cls === 'monk' && sk.type !== 'ultimate') ch.passiveProc = true;
  
  // VFX + 觉醒 cut-in
  const isUltimate = sk.type === 'ultimate';
  if(isUltimate && typeof window.awakeningCutIn === 'function') {
    window.awakeningCutIn(sk.name, ch.cls);
  }
  combatShoot(true, sk.id);
  if(crit) {
    setTimeout(() => { flashCritScreen(); showCombo(2); }, 250);
  }
  
  const hitDelay = isUltimate ? 2000 : 420;
  setTimeout(() => {
    totalDmg = applyBossDR(totalDmg, c);
    c.monHp = Math.max(0, c.monHp - totalDmg);
    c.log.push(`💠 ${sk.name}！${c.monName}受到 <span style="color:var(--red)">${totalDmg}</span> 点伤害${c.st}`);
    c.turn++;
    impactFlash(totalDmg, true, crit); // 碰撞闪光
    animateMonsterHit(totalDmg, true, crit);
    
    // 特效结算
    applySkillEffects(sk, totalDmg, c);
    
    if(c.monHp <= 0) { setTimeout(()=>combatWin(), 400); return; }
    setTimeout(()=>monsterRetaliate(), 500);
  }, hitDelay);
}
} // end combatSkill

// ── 技能效果结算 ──
export function applySkillEffects(sk, dmg, c) {
  if(!sk.effects) return;
  const eff = sk.effects;
  
  // 治疗
  if(eff.heal) {
    const healAmt = Math.min(eff.heal, S.maxHp - S.hp);
    S.hp += healAmt;
    c.log.push(`💚 回复 ${healAmt} HP`);
  }
  if(eff.healPct) {
    const healAmt = Math.floor(S.maxHp * eff.healPct);
    S.hp = Math.min(S.maxHp, S.hp + healAmt);
    c.log.push(`💚 生命完全回复！`);
  }
  if(eff.cleanse) {
    S.statusEffects = {};
    c.log.push('✨ 负面状态已清除');
  }
  if(eff.hot) {
    S._hotHeal = eff.hot.heal;
    S._hotTurns = eff.hot.dur;
    c.log.push(`💚 持续回复 ${eff.hot.heal}HP ×${eff.hot.dur}回合`);
  }
  
  // 自伤
  if(eff.selfDmg) {
    S.hp = Math.max(1, S.hp - eff.selfDmg);
    c.log.push(`🩸 消耗 ${eff.selfDmg} HP`);
  }
  
  // Buff
  if(eff.buff) {
    if(!S._activeBuffs) S._activeBuffs = [];
    eff.buff.forEach(b => {
      S._activeBuffs.push({ stat: b.stat, val: b.val, turns: b.dur });
      const label = b.stat === 'atk' ? '攻击' : '防御';
      c.log.push(`⬆ ${label}+${b.val} (${b.dur}回合)`);
    });
  }
  
  // Debuff (怪物)
  if(eff.debuff) {
    if(!c._debuffs) c._debuffs = {};
    eff.debuff.forEach(db => {
      c._debuffs[db.stat] = { val: db.val, turns: db.dur };
      const emoji = db.val < 0 ? '💔' : '⬇';
      c.log.push(`${emoji} ${c.monName}防御${db.val} (${db.dur}回合)`);
    });
  }
  
  // 晕眩
  if(eff.stun) {
    c.stunTurns = (c.stunTurns || 0) + eff.stun;
    c.log.push(`💫 ${c.monName}被晕眩${eff.stun}回合`);
  }
  
  // 冰冻
  if(eff.freeze) {
    c.stunTurns = (c.stunTurns || 0) + eff.freeze;
    c.log.push(`❄️ ${c.monName}被冰冻${eff.freeze}回合`);
  }
  
  // 灼烧
  if(eff.burn) {
    if(!S._burnTurns) { S._burnTurns = 0; S._burnDmg = 0; }
    S._burnDmg = eff.burn.dmg;
    S._burnTurns = eff.burn.dur;
    c.log.push(`🔥 ${c.monName}被灼烧！(${eff.burn.dmg}/回合 ×${eff.burn.dur})`);
  }
  
  // 诅咒
  if(eff.curse && Math.random() < eff.curse.chance) {
    if(!c._debuffs) c._debuffs = {};
    c._debuffs['def'] = { val: eff.curse.def, turns: eff.curse.dur };
    c.log.push(`🌑 ${c.monName}被诅咒！防御${eff.curse.def}`);
  }
  
  // 生命偷取
  if(eff.lifesteal) {
    const stealAmt = Math.floor(dmg * eff.lifesteal);
    S.hp = Math.min(S.maxHp, S.hp + stealAmt);
    c.log.push(`🩸 生命偷取 +${stealAmt} HP`);
  }
  
  // 格挡护盾
  if(eff.shield) {
    S._shieldTurns = (S._shieldTurns || 0) + eff.shield;
    c.log.push(`🛡️ 格挡姿态！(${eff.shield}回合)`);
  }
  
  // 无敌
  if(eff.invincible) {
    S._invincTurns = (S._invincTurns || 0) + eff.invincible;
    c.log.push(`✨ 无敌状态！(${eff.invincible}回合)`);
  }
  
  // 召唤
  if(eff.summon) {
    S._summonData = { turns: eff.summon.dur, atkPct: eff.summon.atkPct };
    c.log.push(`👿 恶魔降临！持续${eff.summon.dur}回合`);
  }
  
  // 永久Buff
  if(eff.permBuff) {
    S[eff.permBuff.stat] = (S[eff.permBuff.stat] || 0) + eff.permBuff.val;
    c.log.push(`⭐ 攻击力永久+${eff.permBuff.val}`);
  }
  
  // 即死
  if(eff.execute && c.monHp <= c.monMaxHp * eff.execute.hpPct) {
    c.monHp = 0;
    c.log.push('💀 即死发动！目标被瞬间终结！');
  }
  
  // 额外多段打击
  if(eff.bonusHits) {
    let bonusDmg = 0;
    for(let i = 0; i < eff.bonusHits.count; i++) {
      bonusDmg += Math.max(1, Math.floor(getEffAtk() * eff.bonusHits.mult));
    }
    c.monHp = Math.max(0, c.monHp - bonusDmg);
    c.log.push(`💥 额外打击 ${eff.bonusHits.count}段，共${bonusDmg}伤害`);
  }
  
  // 额外百分比伤害
  if(eff.bonusDmg && eff.bonusDmg.hpPct) {
    const extraDmg = Math.floor(S.hp * eff.bonusDmg.hpPct);
    c.monHp = Math.max(0, c.monHp - extraDmg);
    c.log.push(`💥 额外 ${extraDmg} HP真伤`);
  }
  
  // 即死再次检查
  if(eff.execute && c.monHp <= c.monMaxHp * eff.execute.hpPct) {
    c.monHp = 0;
    c.log.push('💀 即死发动！');
  }
}

// ── 怪物反击 ──
export function monsterRetaliate(character) {
  const ch = character || S;
  const c = S.combat;
  if(!c || c.monHp <= 0) return;

  // 存储基础def
  if(!c._baseMonDef) c._baseMonDef = c.monDef;
  
  // 怪物debuff结算
  if(c._debuffs) {
    let defMod = 0;
    for(const stat in c._debuffs) {
      const db = c._debuffs[stat];
      if(db.turns > 0) {
        if(stat === 'def') defMod += db.val;
        db.turns--;
        if(db.turns <= 0) delete c._debuffs[stat];
      }
    }
    c.monDef = Math.max(0, c._baseMonDef + defMod);
  }
  
  // 怪物灼烧DOT
  if(ch._burnTurns > 0) {
    c.monHp = Math.max(0, c.monHp - ch._burnDmg);
    c.log.push(`🔥 灼烧${c.monName}造成 ${ch._burnDmg} 伤害`);
    ch._burnTurns--;
    if(ch._burnTurns <= 0) { ch._burnDmg = 0; }
    if(c.monHp <= 0) { setTimeout(()=>combatWin(), 300); return; }
  }

  // 精英词缀：再生
  if(c._eliteModifier && c._eliteModifier.regen) {
    const regenAmount = c._eliteModifier.regen;
    c.monHp = Math.min(c.monMaxHp, c.monHp + regenAmount);
    c.log.push(`💚 ${c.monName}恢复了 ${regenAmount} HP`);
  }

  if(c.multiPhase && !c.phase2Triggered && c.monHp <= c.phase2Hp) {
    c.phase2Triggered = true;
    c.monAtk += c.phase2AtkBonus;
    
    const phaseTexts = {
      'bone_dragon':   ['💀 腐败的尸堆中涌出亡灵之力！骨狱息吸收满地的骸骨，体型暴增！', `攻击力+${c.phase2AtkBonus}！`],
      'flame_boss':    ['🔥 烈焰彼诺修发出刺耳的尖啸——半个房间化为了熔岩！', `攻击力+${c.phase2AtkBonus}！`],
      'ice_witch':     ['❄️ 冰晶从女巫体内爆裂！她不惜摧毁自己的身体也要阻止你！', `攻击力+${c.phase2AtkBonus}！`],
      'golem_guard':   ['🪨 巨石坠落又重组——地界守卫者以更狰狞的形态站了起来！', `攻击力+${c.phase2AtkBonus}！`],
      'cloud_gargoyle':['🗿 石化的外壳崩裂！石像鬼露出了被封印了千年的恶魔真身！', `攻击力+${c.phase2AtkBonus}！`],
      'spider_queen':  ['🕷️ 蛛巢墙壁渗出毒液——蜘蛛女王吞噬了自己的卵来获取力量！', `攻击力+${c.phase2AtkBonus}！`],
      'shadow_lord':   ['👻 上千条亡魂同时哀嚎！暗影领主释放了所有的怨念——', `攻击力+${c.phase2AtkBonus}！`],
      'sky_dragon':    ['🐉 天空龙王发出震天龙鸣——龙晶碎裂，巴卡尔的残响从远古苏醒！', '「你竟能伤我到如此地步……那么，接受真正的试炼吧！」'],
    };
    
    const texts = phaseTexts[c.monsterId];
    if(texts) {
      c.log.push(`<span style="color:var(--red);font-weight:bold;">${texts[0]}</span>`);
      if(texts[1]) c.log.push(`<span style="color:var(--red);">${texts[1]}</span>`);
    } else {
      c.log.push(`<span style="color:var(--red);font-weight:bold;">⚠ ${c.monName}进入狂暴形态！攻击力+${c.phase2AtkBonus}！</span>`);
    }
    // 阶段转换特效
    screenShake(8);
    const fp = document.createElement('div');
    fp.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(255,0,0,0.1);pointer-events:none;z-index:500;animation:flashHeavy 0.5s ease-out forwards;';
    document.body.appendChild(fp); setTimeout(()=>fp.remove(),550);
    window.renderCombat();
    return;
  }

  if(c.stunTurns > 0) { c.stunTurns--; c.log.push(`${c.monName}被晕眩，无法行动`); window.endOfTurnTick(); window.renderCombat(); renderStatusbar(); save(); return; }

  // ══════════════════════════════════════════
  // Boss 被动+技能 (硬编码防缓存)
  // ══════════════════════════════════════════
  const BOSS_ABI = {
    flame_boss:  { pass:{regen:5}, skill:{name:'烈焰风暴',desc:'全屏焰火AOE，1.5倍伤害',mult:1.5,aoe:true,rate:0.25} },
    bone_dragon: { pass:{dmgReduce:0.25}, skill:{name:'死亡寒气',desc:'冰霜吐息+冰冻',mult:1.3,freeze:1,rate:0.3} },
    golem_guard: { pass:{blockChance:0.5,blockAmount:0.3}, skill:{name:'大地震撼',desc:'防御+5（3回合）',buffSelf:{stat:'def',val:5,dur:3},rate:0.3} },
    ice_witch:   { pass:{shieldCycle:3}, skill:{name:'极寒风暴',desc:'冰冻玩家1回合',stunPlayer:1,rate:0.25} },
    cloud_gargoyle:{ pass:{lowHpDef:true}, skill:{name:'旋风突袭',desc:'一回合攻击两次',doubleAtk:true,rate:0.25} },
    sky_dragon:  { pass:{mpDrain:5}, skill:{name:'龙神之怒',desc:'终极吐息2.0倍',mult:2.0,rate:0.2} },
    spider_queen:{ pass:{poisonProc:0.5}, skill:{name:'蛛网陷阱',desc:'玩家跳过1回合',stunPlayer:1,rate:0.2} },
  };
  const abi = BOSS_ABI[c.monsterId];
  if(c.isBoss && abi) {
    const pass = abi.pass, sk = abi.skill;
    // 火焰之躯 — 每回合回血
    if(pass && pass.regen) {
      c.monHp = Math.min(c.monMaxHp, c.monHp + pass.regen);
      c.log.push(`🔥 ${c.monName}火焰之躯回复 ${pass.regen} HP`);
    }
    // 龙威 — 扣MP
    if(pass && pass.mpDrain) {
      ch.mp = Math.max(0, (ch.mp||0) - pass.mpDrain);
      c.log.push(`🐉 龙威压制！技力 -${pass.mpDrain}`);
    }
    // 石像形态 — 低血量防御翻倍
    if(pass && pass.lowHpDef && c.monHp < c.monMaxHp * 0.3) {
      if(!c._stoneForm) { c._stoneForm = true; c.monDef = Math.floor(c.monDef * 2); c.log.push('🗿 石像形态激活！防御力翻倍！'); }
    }
    // 冰霜护盾循环
    if(pass && pass.shieldCycle) {
      c._shieldCycleTurn = (c._shieldCycleTurn || 0) + 1;
      if(c._shieldCycleTurn >= pass.shieldCycle) {
        c._shieldCycleTurn = 0; c._iceShield = true;
        c.log.push('❄️ 冰霜护盾激活！下一回合免疫伤害');
      }
    }
    // 毒液腺体 — 攻击时额外上毒
    if(pass && pass.poisonProc && Math.random() < pass.poisonProc) {
      if(!S.statusEffects) S.statusEffects = {};
      S.statusEffects.poison = (S.statusEffects.poison||0) + 3;
      c.log.push('☠ 毒液溅射！中毒+3回合');
    }
    
    // Boss 技能
    if(sk && Math.random() < (sk.rate||0.25)) {
      const sk = sk;
      c.log.push(`<span style="color:var(--red);font-weight:bold;">⚠ ${c.monName}释放【${sk.name}】！</span>`);
      c.log.push(`<span style="color:var(--dim);font-size:10px;">${sk.desc}</span>`);
      
      // 冰霜护盾格挡
      if(c._iceShield) { c._iceShield = false; c.log.push('❄️ 但冰霜护盾已消耗，本回合免疫！'); window.renderCombat(); return; }
      
      if(sk.mult) {
        // 伤害型技能
        let skDmg = Math.max(1, Math.floor(c.monAtk * sk.mult) - (typeof ch.getEffDef === 'function' ? ch.getEffDef() : getEffDef()));
        if(sk.freeze) c.stunTurns = (c.stunTurns||0) + sk.freeze;
        ch.hp = Math.max(0, ch.hp - skDmg);
        c.log.push(`造成 <span style="color:var(--red)">${skDmg}</span> 点伤害！${sk.freeze?'❄️ 你被冰冻了！':''}`);
        if(sk.aoe) screenShake(6);
      } else if(sk.buffSelf) {
        // 自身Buff型
        if(!c._bossBuffs) c._bossBuffs = {};
        const b = sk.buffSelf;
        if(b.stat === 'def') { c.monDef += b.val; c._bossBuffs.def = (c._bossBuffs.def||0) + b.val; }
        c.log.push(`🛡️ ${c.monName}的${b.stat==='def'?'防御':b.stat}+${b.val}！`);
      } else if(sk.stunPlayer) {
        // 控制型
        if(!c._playerStunTurns) c._playerStunTurns = 0;
        c._playerStunTurns += sk.stunPlayer;
        c.log.push('🕸️ 你被控制，无法行动！');
        window.renderCombat(); renderStatusbar(); save(); return;
      } else if(sk.doubleAtk) {
        // 双击型 — 攻击两次
        let mdmg1 = Math.max(1, c.monAtk - (typeof ch.getEffDef === 'function' ? ch.getEffDef() : getEffDef()) + Math.floor(Math.random()*7) - 2);
        let mdmg2 = Math.max(1, Math.floor(c.monAtk * 0.7) - (typeof ch.getEffDef === 'function' ? ch.getEffDef() : getEffDef()) + Math.floor(Math.random()*5));
        ch.hp = Math.max(0, ch.hp - mdmg1 - mdmg2);
        c.log.push(`旋风双击造成 <span style="color:var(--red)">${mdmg1}+${mdmg2}</span> 伤害！`);
        screenShake(4);
        window.renderCombat(); renderStatusbar(); save();
        if(ch.hp <= 0) { setTimeout(()=>combatLose(), 400); return; }
        return; // 跳过正常攻击
      }
      
      window.renderCombat(); renderStatusbar(); save();
      if(S.hp <= 0) { setTimeout(()=>combatLose(), 400); return; }
      return; // Boss技能替代了普攻
    }
  }

  // 玩家：护盾/无敌
  if(S._shieldTurns > 0) {
    S._shieldTurns--;
    c.log.push('🛡️ 格挡护盾吸收了所有伤害！');
    window.endOfTurnTick();
    window.renderCombat(); renderStatusbar(); save(); return;
  }
  if(S._invincTurns > 0) {
    S._invincTurns--;
    c.log.push('✨ 无敌状态免疫所有伤害！');
    window.endOfTurnTick();
    window.renderCombat(); renderStatusbar(); save(); return;
  }

  // 怪物攻击
  let mdmg = Math.max(1, c.monAtk - (typeof ch.getEffDef === 'function' ? ch.getEffDef() : getEffDef()) + Math.floor(Math.random()*7) - 2);

  // 旧版被动
  if(S.cls === 'launcher' && Math.random() < 0.25) {
    mdmg = Math.floor(mdmg * 0.5);
    c.log.push('🛡 重装甲触发，伤害减半');
  }
  if(S.cls === 'asura' && Math.random() < 0.25) {
    c.log.push('🌀 波动感知：完美回避！');
    window.endOfTurnTick();
    window.renderCombat(); renderStatusbar(); save(); return;
  }
  if(ch.cls === 'crusader') {
    ch.hp = Math.min(ch.maxHp, ch.hp + 5);
    c.log.push('🛡 圣盾庇护：回复5HP');
  }

  ch.hp = Math.max(0, ch.hp - mdmg);
  c.log.push(`${c.monName}攻击，造成 <span style="color:var(--red)">${mdmg}</span> 点伤害`);
  // 玩家侧碰撞闪光
  setTimeout(() => {
    const pw = document.getElementById('playerWrap');
    if(pw) { const pb = document.createElement('div'); pb.style.cssText = 'position:absolute;top:50%;left:50%;width:50px;height:50px;margin:-25px 0 0 -25px;border-radius:50%;background:radial-gradient(circle,rgba(255,100,80,0.5),transparent 70%);pointer-events:none;z-index:50;animation:deathBurst 0.5s ease-out forwards;'; pw.appendChild(pb); setTimeout(()=>pb.remove(),600); }
  }, 10);

  // 怪物状态攻击
  if(c.monStatusAtk === 'poison' && Math.random() < 0.3) {
    if(!ch.statusEffects) ch.statusEffects = {};
    ch.statusEffects.poison = 3;
    c.log.push('☠ 你中毒了！（持续3回合）');
  }
  if(c.monStatusAtk === 'freeze' && Math.random() < 0.25) {
    c.stunTurns = 1;
    c.log.push('❄ 你被冰冻了！');
  }
  if(c.monStatusAtk === 'curse' && Math.random() < 0.2) {
    if(!S.statusEffects) S.statusEffects = {};
    S.statusEffects.curse = true;
    c.log.push('◈ 你被诅咒了！攻击力减半');
  }

  // 玩家中毒
  if(S.statusEffects && S.statusEffects.poison) {
    const pDmg = 10;
    S.hp = Math.max(0, S.hp - pDmg);
    S.statusEffects.poison--;
    if(S.statusEffects.poison <= 0) delete S.statusEffects.poison;
    c.log.push(`☠ 中毒损失 ${pDmg} HP`);
  }

  // 召唤物攻击
  if(S._summonData && S._summonData.turns > 0) {
    const summonDmg = Math.max(1, Math.floor(getEffAtk() * S._summonData.atkPct));
    c.monHp = Math.max(0, c.monHp - summonDmg);
    c.log.push(`👿 恶魔造成 ${summonDmg} 伤害`);
    S._summonData.turns--;
    if(S._summonData.turns <= 0) { S._summonData = null; c.log.push('👿 恶魔回归地狱'); }
  }

  combatMonsterShoot();
  setTimeout(() => {
    animatePlayerHit(mdmg);
    window.endOfTurnTick();
    if(S.hp <= 0) { setTimeout(()=>combatLose(), 400); return; }
    window.renderCombat(); renderStatusbar(); save();
  }, 250);
}

// ── 使用药水 ──
export function combatPotion() {
  const c = S.combat;
  let potionIdx = -1;
  for(let i = 0; i < (S.inventory||[]).length; i++) {
    if(CONSUMABLES[S.inventory[i].id]) { potionIdx = i; break; }
  }
  if(potionIdx === -1) { toast('没有可用药水'); return; }

  const inv = S.inventory[potionIdx];
  const it = CONSUMABLES[inv.id];

  if(it.mpHeal) {
    S.mp = Math.min(S.maxMp||50, (S.mp||0) + it.mpHeal);
    c.log.push(`你使用了 <span style="color:var(--blue)">${it.name}</span>，恢复 ${it.mpHeal} 技力`);
  } else if(it.cure && S.statusEffects && S.statusEffects[it.cure]) {
    delete S.statusEffects[it.cure];
    c.log.push(`你使用了 <span style="color:var(--green)">${it.name}</span>，解除了中毒`);
  } else if(it.heal) {
    const oldHp = S.hp;
    S.hp = Math.min(S.maxHp, S.hp + it.heal);
    c.log.push(`你使用了 <span style="color:var(--green)">${it.name}</span>，恢复 ${S.hp - oldHp} HP`);
  }

  S.inventory.splice(potionIdx, 1);
  window.renderCombat(); renderStatusbar(); save();

  setTimeout(()=>monsterRetaliate(), 500);
}

// ── 怪物受击动画 ──
export function animateMonsterHit(dmg, isSkill, isCrit) { const el = document.getElementById('monsterWrap');
  if(!el) return;
  el.classList.remove('shake','hit-flash');
  void el.offsetWidth;
  el.classList.add('shake','hit-flash');
  // Impact shockwave ring
  const burst = document.getElementById('hitBurst');
  if(burst) {
    burst.classList.remove('show','skill');
    if(dmg && dmg > 15) burst.classList.add('show');
    if(isSkill || (dmg && dmg > 20)) burst.classList.add('skill');
    void burst.offsetWidth;
    if(dmg && dmg > 15) burst.classList.add('show');
  }
  // 使用增强特效系统
  animateMonsterHitEnhanced(dmg, !!isSkill, !!isCrit, S.cls);
  sfxHeavyHit();
  hitStop((isSkill||dmg>25) ? 100 : 60);
  if(dmg) showDmgNumber(el, dmg, (isCrit?'#ffdd00':'#ff6644'));
}

// ── 玩家受击动画 ──
export function animatePlayerHit(dmg) { if(!S.combat) return;
  const el = document.getElementById('playerWrap');
  if(!el) return;
  const cls = S.cls;
  const color = CLASS_COLORS[cls] || '#ff4444';
  
  // 抖动+闪白
  el.classList.remove('shake','hit-flash');
  void el.offsetWidth;
  el.classList.add('shake','hit-flash');
  
  // 大伤害 = 击退微移
  if(dmg > 15) {
    el.style.transition = 'none';
    el.style.transform = 'translateX(-6px)';
    setTimeout(() => { el.style.transition = 'transform 0.3s ease-out'; el.style.transform = ''; }, 60);
  }
  
  // 玩家受击粒子
  burstParticles(el, '#ff6666', dmg > 15 ? 10 : 5, dmg > 20 ? 5 : 2);
  
  // 屏幕微震
  if(dmg > 12) screenShake(dmg > 25 ? 4 : 2);
  
  sfxHit();
  if(dmg) showDmgNumber(el, dmg, '#ff4444');
}

// ══════════════════════════════════════════
// 碰撞闪光 — 弹道命中瞬间的冲击点爆发
// ══════════════════════════════════════════
export function impactFlash(dmg, isSkill, isCrit) {
  const burst = document.getElementById('hitBurst');
  if(!burst) return;
  
  // 冲击波环
  burst.classList.remove('show','skill');
  void burst.offsetWidth;
  if(dmg > 8) burst.classList.add('show');
  if(isSkill || dmg > 20) burst.classList.add('skill');
  
  // 大伤害调整冲击波大小
  if(dmg > 30) burst.style.setProperty('--is', '5');
  else if(dmg > 15) burst.style.setProperty('--is', '3.5');
  else burst.style.setProperty('--is', '2');
  
  // 暴击追加金色闪光环
  if(isCrit) {
    const goldRing = document.createElement('div');
    goldRing.style.cssText = `position:absolute;top:50%;left:50%;width:60px;height:60px;margin:-30px 0 0 -30px;border-radius:50%;border:2px solid #ffd700;pointer-events:none;z-index:51;animation:deathBurst 0.6s ease-out forwards;box-shadow:0 0 30px #ffd700,0 0 80px #ff8c00;`;
    document.getElementById('combatIllust')?.appendChild(goldRing);
    setTimeout(() => goldRing.remove(), 700);
  }
}

// ── 发射弹道特效 ──
export function combatShoot(isSkill, skillId) { if(!S.combat) return;
  const cls = S.cls;
  let skType = 'power';
  if(skillId && SKILLS_DB[skillId]) skType = SKILLS_DB[skillId].type || 'power';
  playClassAtkSound(cls, isSkill, skType);
  if(isSkill) window.flashScreen();
  const proj = document.getElementById('projectile');
  if(!proj) return;

  // 增强技能特效（蓄力+技能名+全屏闪光+能量射线）
  if(isSkill && skType) {
    triggerSkillVFX(cls, true);
  }

  // 鬼泣 - 无弹道，直接画斩击痕迹
  if(cls === 'ghost') {
    showGhostSlash(isSkill);
    return;
  }

  // 其他职业：发射弹道
  proj.className = 'projectile';
  void proj.offsetWidth;

  // 根据职业选择专属效果
  const clsEffect = getClassEffect(cls, isSkill);
  proj.classList.add(clsEffect.cssClass);
  proj.style.animation = 'none';
  void proj.offsetWidth;
  proj.style.animation = clsEffect.anim;
  proj.style.left = '';
  proj.style.right = '';
  if(isSkill) projectileTrail(cls); // 弹道拖尾粒子
  proj.style.boxShadow = '';
}

// ── 鬼泣斩击 ──
export function showGhostSlash(skill) {
  const container = document.getElementById('combatIllust');
  if(!container) return;
  // Remove old slash
  const old = container.querySelector('.ghost-slash-container');
  if(old) old.remove();
  // Create new slash
  const div = document.createElement('div');
  div.className = 'ghost-slash-container';
  // 技能使用增强版七闪斩击
  const slashClass = skill ? 'ghost-slash ghost-slash-skill' : 'ghost-slash';
  var slashesHTML = '';
  if(skill) {
    // 七道暗影斩击——交错切割
    for(var i=0; i<7; i++) {
      var delay = i * 0.04;
      var topOffset = 25 + Math.random() * 25;
      var rotOffset = -55 + i * 15;
      slashesHTML += '<div class="'+slashClass+'" style="top:'+topOffset+'%;transform:rotate('+rotOffset+'deg);animation-delay:'+delay+'s;"></div>';
    }
  } else {
    slashesHTML = '<div class="'+slashClass+'" style="top:40%;transform:rotate(-50deg);"></div>';
  }
  div.innerHTML = slashesHTML;
  container.appendChild(div);
  // Trigger animation
  var slashes = div.querySelectorAll('.ghost-slash');
  for(var j=0; j<slashes.length; j++) {
    void slashes[j].offsetWidth;
    slashes[j].classList.add('show');
  }
  // Clean up after animation
  setTimeout(() => div.remove(), 600);
}

// ── 怪物弹道 ──
export function combatMonsterShoot() { if(!S.combat) return;
  const proj = document.getElementById('projectile');
  if(!proj) return;
  proj.className = 'projectile';
  void proj.offsetWidth;
  proj.classList.add('projectile-attack');
  proj.style.animation = 'none';
  proj.style.left = 'auto';
  proj.style.right = '20px';
  void proj.offsetWidth;
  proj.style.animation = 'monsterProjectileFly 0.35s ease forwards';
}

// ── 逃跑 ──
export function combatFlee() {
  const c = S.combat;
  const inDungeon = S._dungeonRooms && c.dungeonRoomIdx !== undefined;
  
  if(Math.random() < 0.5) {
    toast('逃跑成功！');
    S.combat = null;
    resumeSceneBGM();
    if(inDungeon) {
      // 回到地下城房间视图
      renderDungeonRoom(S._dungeonRooms[c.dungeonRoomIdx], c.dungeonRoomIdx);
    } else {
      S.scene = c.parentScene;
      renderScene();
    }
  } else {
    const mdmg = Math.max(2, c.monAtk - Math.floor(getEffDef()/2));
    S.hp = Math.max(0, S.hp - mdmg);
    c.log.push(`逃跑失败！${c.monName}追击造成 <span style="color:var(--red)">${mdmg}</span> 点伤害`);
    if(S.hp <= 0) { combatLose(); return; }
    window.renderCombat();
  }
  renderStatusbar(); save();
}

// ── 战斗胜利 ──
export function combatWin() {
  const c = S.combat;
  monsterDeathBurst(); // 死亡爆发特效
  let goldMult = 1.0;
  if(S._hellMode) goldMult = 1.5;
  // 地下城修饰符金币加成
  if(S._dungeonModifier) goldMult *= S._dungeonModifier.goldMult || 1;
  
  // 精英怪奖励加成
  const eliteBonus = c.isElite ? 1.3 : 1.0;
  goldMult *= eliteBonus;

  const gold = Math.floor((c.monGold + Math.floor(Math.random()*c.monGold*0.7)) * goldMult);
  let exp = Math.floor((c.monExp + Math.floor(Math.random()*c.monExp*0.4)) * (S._dungeonModifier?.expMult||1));
  if(c.isElite) exp = Math.floor(exp * 1.3);
  S.gold += gold;
  S.exp += exp;
  S.mp = Math.min(S.maxMp||50, (S.mp||0) + 10);

  // 装备掉落 - BOSS专属掉落优先
  let dropMsg = '';
  if(c.isBoss && BOSS_DROPS[c.monId] && Math.random() < BOSS_DROPS[c.monId].rate) {
    const bDrop = BOSS_DROPS[c.monId];
    const eq = ALL_EQUIPMENT.find(e => e.id === bDrop.item);
    if(eq && !S.inventory.find(inv => inv && inv.id === eq.id)) {
      S.inventory.push({ id:eq.id, type:'equip', equipped:false });
      dropMsg = `\n🎁 ${bDrop.msg}\n<span style="color:${TIERS[eq.tier].color}">获得专属装备：[${TIERS[eq.tier].name}] ${eq.name}！</span>`;
    }
  }
  
  // 标准装备掉落
  if(!dropMsg) {
    let dropProb = (S._hellMode ? 1.3 : 1.0) * (S._dungeonModifier?.dropMult||1);
    if(c.isElite) dropProb *= 1.3;
    if(Math.random() < dropProb) {
      const dropItem = rollEquipmentDrop(c.monLv);
      if(dropItem) {
        dropMsg = `\n🎁 获得装备：<span style="color:${TIERS[dropItem.tier].color}">[${TIERS[dropItem.tier].name}] ${dropItem.name}</span>！`;
      }
    }
  }

  // 清除战斗状态
  const wasDungeonCombat = S._dungeonRooms && c.dungeonRoomIdx !== undefined;
  const dungeonRoomIdx = c.dungeonRoomIdx;
  
  S.combat = null;
  resumeSceneBGM();
  S._hellMode = (S._hellMode); // preserve for dungeon continuity
  if(S.statusEffects) { S.statusEffects = {}; }
  S.passiveProc = false;
  sfxMonsterDeath(S.combat?.mon?.id);
  setTimeout(() => sfxUISuccess(), 400);
  checkLevelUp();

  const main = document.getElementById('main');
  const bossBadge = c.isBoss ? '👑 BOSS击败！' : '';
  const eliteBadge = c.isElite ? '👹 精英怪击败！' : '';
  const hellBadge = (S._hellMode) ? '💀 恶魔难度' : '';
  const modifierBadge = S._dungeonModifier ? ` [${S._dungeonModifier.icon} ${S._dungeonModifier.name}]` : '';

  // 地下城房间进度条
  let progressHTML = '';
  if(wasDungeonCombat && S._dungeonRooms) {
    progressHTML = `<div class="dungeon-progress" style="display:flex;gap:4px;justify-content:center;margin-bottom:10px;flex-wrap:wrap;">`;
    S._dungeonRooms.forEach((r, i) => {
      const typeDef = ROOM_TYPES[r.type] || { icon:'⚔️' };
      const isCurrent = i === dungeonRoomIdx;
      const isCleared = r.cleared || (c.isBoss && i === dungeonRoomIdx);
      let bg = isCleared ? 'var(--green)' : '#333';
      let opacity = (i > dungeonRoomIdx && !isCleared) ? '0.3' : '1';
      progressHTML += `<span style="display:inline-block;width:28px;height:28px;line-height:28px;text-align:center;border-radius:50%;background:${bg};opacity:${opacity};font-size:14px;">${isCleared?'✓':typeDef.icon}</span>`;
    });
    progressHTML += `</div>`;
  }

  // 标记房间已清除
  if(wasDungeonCombat && S._dungeonRooms && dungeonRoomIdx < S._dungeonRooms.length) {
    S._dungeonRooms[dungeonRoomIdx].cleared = true;
    S._dungeonRoomCleared = (S._dungeonRoomCleared||0) + 1;
  }

  // Boss击杀 → 完成副本
  if(S.currentDungeon && c.isBoss) {
    completeDungeon(S.currentDungeon.id, S.currentDungeonDiff);
  }

  // 任务进度追踪
  if(typeof updateQuestProgress === 'function') {
    updateQuestProgress('kill', c.monsterId, 1);
    if(c.isBoss) updateQuestProgress('kill_boss', null, 1);
    if(S.currentDungeon && c.isBoss) {
      const regId = getCurrentRegionId();
      if(regId) updateQuestProgress('clear_dungeon', regId, 1);
    }
    checkQuestCompletion();
  }

  // 诺顿Buff清除
  if(S._nortonBuff) {
    if(S._nortonBuff.atkBuff) { S.atk = Math.floor(S.atk / 1.5); }
    if(S._nortonBuff.defBuff) { S.def = Math.floor(S.def / 1.5); }
    if(S._nortonBuff.spdBuff) { S.spd = Math.floor(S.spd / 1.5); }
    S._nortonBuff = {};
  }

  // 地下城战斗：显示房间式结果
  if(wasDungeonCombat && S._dungeonRooms) {
    const victoryImg = getVictoryImage(c.monsterId);
    main.innerHTML = `
      <div class="story-box">
        <strong style="color:var(--green);font-size:15px;">战斗胜利！${bossBadge ? '<br>'+bossBadge : ''}${eliteBadge ? '<br>'+eliteBadge : ''}</strong>
        ${hellBadge ? `<div style="color:var(--red);font-size:11px;">${hellBadge}${modifierBadge}</div>` : ''}
        <div style="text-align:center;">${victoryImg}</div>
        ⚔️ 击败了 ${c.monName}
        💰 获得 ${gold} 金币
        ✨ 获得 ${exp} 经验值${dropMsg}
      </div>
      ${progressHTML}`;

    if(c.isBoss) {
      // Boss击败：调用胜利面板（含插画、再次挑战、下一关、流浪商人）
      window.renderBossVictory(c, gold, exp, dropMsg, progressHTML, true);
    } else {
      // 普通战斗：继续探索
      const targetRoomIdx = (S._nextDungeonRoomAfterCombat !== undefined && S._nextDungeonRoomAfterCombat !== null) 
        ? S._nextDungeonRoomAfterCombat : (dungeonRoomIdx + 1);
      
      // 清除旅行战斗标记
      const wasTravelCombat = S._nextDungeonRoomAfterCombat !== undefined && S._nextDungeonRoomAfterCombat !== null;
      S._nextDungeonRoomAfterCombat = null;
      
      main.innerHTML += `<button class="continue-btn" onclick="goToDungeonRoom(${targetRoomIdx})">▶ 继续探索下一个房间</button>`;
      main.innerHTML += `<button class="continue-btn" style="margin-top:4px;background:rgba(201,164,75,0.08);" onclick="goHome()">🏠 返回大厅（保存进度）</button>`;
      
      if(wasTravelCombat) {
        main.innerHTML += `<div style="font-size:10px;color:var(--dim);margin-top:4px;text-align:center;">⚠ 别忘了还有刚才遇到的房间没探索</div>`;
      }
    }
    
    // 15%概率在普通战斗后触发小事件
    if(!c.isBoss && Math.random() < 0.15) {
      setTimeout(() => {
        const bonusEvents = [
          { text:'战后搜索，发现了散落的金币', gold:30+Math.floor(Math.random()*50) },
          { text:'怪物巢穴里有几株药草', item:'hp_potion_s' },
          { text:'墙壁缝隙中有一枚古老金币', gold:80+Math.floor(Math.random()*100) },
        ];
        const evt = bonusEvents[Math.floor(Math.random()*bonusEvents.length)];
        let msg = '';
        if(evt.gold) { S.gold += evt.gold; msg = `💰 +${evt.gold}G`; }
        if(evt.item) { addItem(evt.item); msg = `🧪 获得药草`; }
        const bonusEl = document.createElement('div');
        bonusEl.className = 'story-box';
        bonusEl.style.cssText = 'border-color:var(--gold);margin-top:6px;animation:fadeIn 0.3s ease;';
        bonusEl.innerHTML = `<div style="color:var(--gold);font-size:12px;">🔍 ${evt.text}</div><div style="color:var(--gold);">${msg}</div>`;
        main.appendChild(bonusEl);
        save();
      }, 800);
    }
  } else {
    // 非地下城战斗（旧系统）- 保持兼容，但Boss也用新面板
    if(c.isBoss) {
      window.renderBossVictory(c, gold, exp, dropMsg, '', false);
    } else {
      const targetScene = c.parentScene || 'lobby';
      const victoryImg = getVictoryImage(c.monsterId);
      main.innerHTML = `
        <div class="story-box">
          <strong style="color:var(--green);font-size:15px;">战斗胜利！</strong>
          <div style="text-align:center;">${victoryImg}</div>
          ⚔️ 击败了 ${c.monName}
          💰 获得 ${gold} 金币
          ✨ 获得 ${exp} 经验值${dropMsg}
        </div>
        <button class="continue-btn" onclick="window.goAfterCombat('${targetScene}')">继续前行</button>`;
    }

  renderStatusbar(); save();
  
  // 保存区域进度
  if(!wasDungeonCombat) {
    const targetScene = c.parentScene || 'lobby';
    if(targetScene !== 'lobby') {
      const curRegion = getCurrentRegionId();
      if(curRegion) S.regionProgress[curRegion] = targetScene;
    }
  }
}
}

// ── 战斗失败 ──
export function combatLose() {
  const c = S.combat;
  S.combat = null;
  resumeSceneBGM();
  if(S.statusEffects) S.statusEffects = {};
  S.passiveProc = false;
  S.hp = Math.floor(S.maxHp * 0.3);
  const goldLost = Math.floor(S.gold * 0.1);
  S.gold = Math.max(0, S.gold - goldLost);

  const main = document.getElementById('main');
  main.innerHTML = `
    <div class="story-box">
      <strong style="color:var(--red);font-size:15px;">战斗失败…</strong>

      你被 ${c.monName} 击败，昏迷了过去。
      醒来时发现金币少了 ${goldLost} 枚。
      赛丽亚帮你处理了伤口。

      💡 提示：尝试提升等级、更换装备、购买药剂
    </div>
    <button class="continue-btn" onclick="goHome()">返回大厅</button>
  `;
  renderStatusbar(); save();
}

// ── 回合末结算 ──
export function endOfTurnTick() {
  // MP回复：每回合恢复10%
  S.mp = Math.min(S.maxMp||50, (S.mp||0) + Math.ceil((S.maxMp||50) * 0.1));

  // 冷却递减
  if(S._skillCooldowns) {
    for(const sid in S._skillCooldowns) {
      if(S._skillCooldowns[sid] > 0) S._skillCooldowns[sid]--;
    }
  }

  // Buff递减
  if(S._activeBuffs) {
    S._activeBuffs = S._activeBuffs.filter(b => {
      b.turns--;
      return b.turns > 0;
    });
  }

  // HOT
  if(S._hotTurns > 0) {
    S.hp = Math.min(S.maxHp, S.hp + S._hotHeal);
    S._hotTurns--;
  }
}

// ── 可用技能 ──
export function getAvailableSkills() {
  const cls = CLASSES[S.cls];
  if(!cls || !cls.skills) return [];
  if(!S._skillCooldowns) S._skillCooldowns = {};
  if(!S._skillUpgrades) S._skillUpgrades = {};

  return cls.skills.map((sid, idx) => {
    const sk = SKILLS_DB[sid];
    if(!sk) return null;
    const isUpgraded = S._skillUpgrades[sid];
    const data = isUpgraded && sk.upgraded ? { ...sk, ...sk.upgraded, isUpgraded: true } : { ...sk, isUpgraded: false };
    const unlocked = S.lv >= sk.unlockLv;
    const cooldown = S._skillCooldowns[sid] || 0;
    const hasMp = (S.mp || 0) >= (data.cost || 0);
    return { ...data, index: idx, unlocked, cooldown, hasMp, ready: unlocked && cooldown === 0 && hasMp };
  }).filter(Boolean);
}

// ── 获取技能数据 ──
export function getSkillData(skillId) {
  const sk = SKILLS_DB[skillId];
  if(!sk) return null;
  if(!S._skillUpgrades) S._skillUpgrades = {};
  const isUpgraded = S._skillUpgrades[skillId];
  if(isUpgraded && sk.upgraded) return { ...sk, ...sk.upgraded, isUpgraded: true, id: skillId };
  return { ...sk, isUpgraded: false, id: skillId };
}

// ── INT乘数 ──
export function getIntMultiplier() {
  const stats = getEquipStats();
  return stats.bonusInt / 10;
}

// ── 职业弹道特效 ──
export function getClassEffect(cls, skill) {
  const map = {
    sword:     { cssClass: skill ? 'proj-sword-skill' : 'proj-sword',     anim: skill ? 'swordSlashV3 0.55s ease-out forwards' : 'projFlyFast 0.4s ease forwards' },
    berserk:   { cssClass: skill ? 'proj-berserk-skill' : 'proj-berserk', anim: skill ? 'berserkCrashV3 0.6s cubic-bezier(0.5,0,0.8,0.5) forwards' : 'projFlySpin 0.4s ease forwards' },
    ghost:     { cssClass: 'proj-ghost-skill',/*handled via DOM*/           anim: 'none' },
    asura:     { cssClass: skill ? 'proj-asura-skill' : 'proj-asura',     anim: skill ? 'asuraWaveV3 0.6s ease-out forwards' : 'projFlyGround 0.4s ease forwards' },
    ranger:    { cssClass: skill ? 'proj-ranger-skill' : 'proj-ranger',   anim: skill ? 'rangerScatterV3 0.5s ease-out forwards' : 'projFlyFast 0.35s ease forwards' },
    launcher:  { cssClass: skill ? 'proj-launcher-skill' : 'proj-launcher',anim: skill ? 'launcherBeamV3 0.5s ease-out forwards' : 'projectileFly 0.4s ease forwards' },
    crusader:  { cssClass: skill ? 'proj-crusader-skill' : 'proj-crusader',anim: skill ? 'holyDescendV3 0.6s ease-in forwards' : 'projectileFly 0.4s ease forwards' },
    monk:      { cssClass: skill ? 'proj-monk-skill' : 'proj-monk',       anim: skill ? 'monkBurstV3 0.55s ease-out forwards' : 'projFlySpin 0.4s ease forwards' },
  };
  return map[cls] || { cssClass: skill ? 'projectile-skill' : 'projectile-attack', anim: skill ? 'skillFly 0.6s ease forwards' : 'projectileFly 0.4s ease forwards' };
}

// ── 冲击粒子 ──
export function spawnHitParticles(el, color) {
  if(!el) return;
  for(let i=0;i<5;i++) {
    const p = document.createElement('div');
    p.className = 'hit-particle';
    p.style.cssText = `position:absolute;width:4px;height:4px;background:${color};border-radius:50%;pointer-events:none;z-index:40;
      left:${30+Math.random()*40}%;top:${30+Math.random()*40}%;
      animation: particleBurst 0.5s ease-out forwards;
      animation-delay:${i*0.03}s;`;
    el.appendChild(p);
    setTimeout(()=>p.remove(), 600);
  }
}

// ── 全屏闪光 ──
export function flashScreen() {
  const app = document.getElementById('app');
  if(!app) return;
  app.style.transition = 'none';
  app.style.filter = 'brightness(1.3)';
  setTimeout(()=>{ app.style.transition='filter 0.3s ease-out'; app.style.filter='brightness(1)'; }, 50);
}

// ── 觉醒插图获取（支持管理后台配置） ──
const AWA_FILE_MAP = { soulbender:'ghost', blademaster:'sword' };
function getAwakenImage(clsId) {
  const adminCfg = window.__adminCfg || {};
  const awaCfg = adminCfg.awaImages || {};
  const cfg = awaCfg[clsId] || {};
  // 优先使用管理后台配置，否则用默认文件名（处理ID→文件名映射）
  const defaultImg = `assets/awakening_${AWA_FILE_MAP[clsId] || clsId}.png`;
  return cfg.a3 || cfg.a2 || cfg.a1 || defaultImg;
}

// ── 觉醒 cut-in ──
export function awakeningCutIn(skillName, clsId) {
  const cls = CLASSES[clsId];
  if(!cls) return;
  const overlay = document.createElement('div');
  overlay.className = 'awakening-overlay ' + clsId;

  // 粒子
  let particlesHtml = '';
  for(let i = 0; i < 50; i++) {
    const x = 40 + Math.random() * 20;
    const y = 30 + Math.random() * 40;
    const dx = (Math.random() - 0.5) * 500;
    const dy = (Math.random() - 0.5) * 400;
    const size = 1.5 + Math.random() * 4;
    const delay = 0.1 + Math.random() * 0.5;
    const rot = Math.floor(Math.random() * 360);
    const alpha = 0.3 + Math.random() * 0.7;
    particlesHtml += `<div class="aw-particle" style="left:${x}%;top:${y}%;width:${size}px;height:${size}px;opacity:${alpha};--dx:${dx}px;--dy:${dy}px;--rot:${rot}deg;animation-delay:${delay}s;"></div>`;
  }

  // 横扫光线
  let linesHtml = '';
  for(let i = 0; i < 3; i++) {
    const ly = 35 + i * 15;
    const ldelay = 0.15 + i * 0.08;
    linesHtml += `<div class="aw-line" style="top:${ly}%;left:0;width:100%;animation-delay:${ldelay}s;"></div>`;
  }

  overlay.innerHTML = `<div class="awakening-bg"></div>
    <div class="awakening-flash"></div>
    <div class="awakening-vignette"></div>
    ${linesHtml}
    <div class="awakening-particles">${particlesHtml}</div>
    <div class="awakening-portrait-wrap">
      <div class="awakening-aura"></div>
      <img class="awakening-portrait" src="${getAwakenImage(clsId)}" onerror="this.style.display='none'" alt="${cls.name}">
      <div class="awakening-nameplate">
        <div class="awakening-subtitle">AWAKENING</div>
        <div class="awakening-skill-name">${skillName}</div>
      </div>
    </div>`;

  document.body.appendChild(overlay);

  // 屏幕震动
  setTimeout(() => {
    const app = document.getElementById('app');
    if(app) { app.style.animation = 'awShake 0.4s ease-out'; setTimeout(()=>app.style.animation='', 500); }
  }, 150);

  // 2.5秒后淡出
  setTimeout(() => {
    overlay.style.opacity='0';
    overlay.style.transition='opacity 0.35s';
    setTimeout(()=>overlay.remove(), 400);
  }, 2200);
}

// ── 升级检测 ──
export function checkLevelUp() {
  while(S.exp >= S.expNeed && S.lv < 60) {
    S.exp -= S.expNeed;
    S.lv++;
    S.maxHp += 15;
    S.hp = S.maxHp;
    S.maxMp = (S.maxMp||50) + 3;
    S.mp = S.maxMp;
    S.atk += 3;
    S.def += 1;
    S.spd += 1;
    S.expNeed = Math.floor(S.expNeed * 1.5);
    sfxLevelUp();
    toast(`🎉 升级！达到 Lv.${S.lv}`, 2000);
    if(S.lv === 50) {
      setTimeout(() => {
        const colorMap = { sword:'#88ccff', berserk:'#ff5544', ghost:'#aa55ff', asura:'#ffaa33', ranger:'#ffdd44', launcher:'#ff8833', crusader:'#ffddaa', monk:'#cc55ff' };
        const ak = SKILLS_DB[S.cls+'_awaken'];
        if(ak) {
          window.showAwakenCutIn(S.cls, ak.name, colorMap[S.cls]||'#ffaa33');
          setTimeout(() => toast('🌟 觉醒技能解锁！可在战斗中释放', 3000), 2000);
        }
      }, 500);
    }
    checkSkillUnlocks(S.lv);
  }
}

// ── 技能解锁检测 ──
export function checkSkillUnlocks(newLv) {
  const cls = CLASSES[S.cls];
  if(!cls || !cls.skills) return;
  if(!S._skillUpgrades) S._skillUpgrades = {};
  cls.skills.forEach(sid => {
    const sk = SKILLS_DB[sid];
    if(!sk) return;
    if(newLv === sk.unlockLv) {
      setTimeout(() => toast(`✨ 新技能解锁：${sk.name}！`, 2500), 1200);
    }
    if(newLv >= sk.upgradeLv && !S._skillUpgrades[sid]) {
      S._skillUpgrades[sid] = true;
      const upName = (sk.upgraded && sk.upgraded.name) ? sk.upgraded.name : sk.name;
      setTimeout(() => toast(`⭐ 技能进化：${sk.name} → ${upName}！`, 3000), 1500);
    }
  });
}

// ══════════════════════════════════════════
// 技能增强 VFX 系统 (之前缺失，CSS 已就绪)
// ══════════════════════════════════════════

const CLASS_COLORS = {
  sword:'#88ccff', berserk:'#ff5544', ghost:'#aa55ff', asura:'#ffaa33',
  ranger:'#ffdd44', launcher:'#ff8833', crusader:'#ffddaa', monk:'#cc55ff'
};

// ══════════════════════════════════════════
// 屏幕震动 (强度 1-10)
// ══════════════════════════════════════════
export function screenShake(intensity) {
  const app = document.getElementById('app') || document.body;
  const lvl = Math.min(10, Math.max(1, intensity||5));
  const dur = 0.3 + lvl * 0.05;
  const amp = lvl * 1.2;
  const keyframes = [
    {transform:'translate(0,0)'},
    {transform:`translate(${-amp}px,${amp*0.4}px)`,offset:0.1},
    {transform:`translate(${amp*0.8}px,${-amp*0.5}px)`,offset:0.25},
    {transform:`translate(${-amp*0.5}px,${-amp*0.3}px)`,offset:0.4},
    {transform:`translate(${amp*0.3}px,${amp*0.2}px)`,offset:0.6},
    {transform:'translate(0,0)',offset:1}
  ];
  app.animate(keyframes, {duration:dur*1000, easing:'ease-out'});
}

// ══════════════════════════════════════════
// 多层粒子爆发 (火花 + 烟雾 + 能量碎片)
// ══════════════════════════════════════════
export function burstParticles(el, color, count, intensity) {
  if(!el) return;
  const n = count || 15;
  const spread = (intensity||5) * 10;
  
  for(let i = 0; i < n; i++) {
    const size = 2 + Math.random() * (4 + n/10);
    const dx = (Math.random() - 0.5) * spread * 2;
    const dy = -(Math.random() * spread + spread/2);
    const isSpark = i % 3 === 0;
    const p = document.createElement('div');
    p.className = isSpark ? 'skill-particle' : 'hit-particle';
    p.style.cssText = `position:absolute;width:${size}px;height:${isSpark?size:size*0.6}px;background:${color};opacity:${0.5+Math.random()*0.5};border-radius:${isSpark?'1px':'50%'};pointer-events:none;z-index:40;left:${35+Math.random()*30}%;top:${35+Math.random()*30}%;--sx:${dx}px;--sy:${dy}px;--sr:${Math.floor(Math.random()*180)}deg;animation:sparkBurst ${0.5+Math.random()*0.4}s ease-out forwards;animation-delay:${i*0.015}s;`;
    el.appendChild(p);
    setTimeout(() => p.remove(), 700 + i*15);
  }
  
  // 烟雾 puff (大伤害)
  if(spread > 50) {
    for(let i = 0; i < 3; i++) {
      const sp = document.createElement('div');
      sp.style.cssText = `position:absolute;width:20px;height:20px;background:radial-gradient(circle,${color}44,transparent 70%);border-radius:50%;pointer-events:none;z-index:35;left:${40+Math.random()*20}%;top:${40+Math.random()*20}%;--smx:${(Math.random()-0.5)*40}px;--smy:${-20-Math.random()*30}px;animation:smokePuff 0.8s ease-out forwards;animation-delay:${i*0.06}s;`;
      el.appendChild(sp);
      setTimeout(() => sp.remove(), 900);
    }
  }
}

// ══════════════════════════════════════════
// 能量聚集 (技能释放前奏)
// ══════════════════════════════════════════
export function energyGather(cls) {
  const color = CLASS_COLORS[cls] || '#ffd700';
  const body = document.body;
  
  // 创建聚集粒子环
  const ring = document.createElement('div');
  ring.style.cssText = `position:fixed;top:30%;left:20%;width:120px;height:120px;border-radius:50%;border:2px solid ${color}44;pointer-events:none;z-index:100;transform:translate(-50%,-50%);animation:energyGather 0.6s ease-out forwards;box-shadow:0 0 30px ${color}33,inset 0 0 30px ${color}22;`;
  body.appendChild(ring);
  setTimeout(() => ring.remove(), 700);
  
  // 多个光点向中心汇聚
  for(let i = 0; i < 8; i++) {
    const angle = (i/8)*Math.PI*2;
    const dist = 80 + Math.random()*40;
    const x = Math.cos(angle)*dist + 120;
    const y = Math.sin(angle)*dist + 50;
    const dot = document.createElement('div');
    dot.style.cssText = `position:fixed;left:20%;top:30%;width:4px;height:4px;background:${color};border-radius:50%;pointer-events:none;z-index:101;box-shadow:0 0 6px ${color};animation:sparkBurst 0.5s ease-in forwards;--sx:${-x}px;--sy:${-y}px;opacity:0.8;`;
    body.appendChild(dot);
    setTimeout(() => dot.remove(), 600);
  }
}

// ══════════════════════════════════════════
// 暴击全屏闪光 (增强版)
// ══════════════════════════════════════════
export function flashCritScreen() {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:radial-gradient(circle,rgba(255,240,200,0.4) 0%,rgba(255,200,50,0.15) 50%,transparent 80%);pointer-events:none;z-index:999;animation:hitBurst 0.6s ease-out forwards;';
  document.body.appendChild(overlay);
  setTimeout(() => overlay.remove(), 650);
}

// ══════════════════════════════════════════
// 连击数字弹出
// ══════════════════════════════════════════
let comboCount = 0;
let comboTimer = null;
export function showCombo(n) {
  comboCount += (n||1);
  let el = document.getElementById('comboCounter');
  if(!el) {
    el = document.createElement('div');
    el.id = 'comboCounter';
    el.style.cssText = 'position:fixed;top:30%;right:15%;z-index:200;pointer-events:none;font-size:36px;font-weight:900;color:#ffd700;text-shadow:0 0 20px #ffd700,0 0 40px #ff8c00,0 3px 6px rgba(0,0,0,0.8);opacity:0;transform:scale(0.5);transition:all 0.15s cubic-bezier(0.16,1,0.3,1);font-family:sans-serif;';
    document.body.appendChild(el);
  }
  el.textContent = comboCount > 1 ? `×${comboCount}` : 'CRITICAL';
  el.style.opacity = '1';
  el.style.transform = 'scale(1.4)';
  el.style.fontSize = comboCount > 3 ? '48px' : '36px';
  setTimeout(() => { el.style.transform = 'scale(1)'; el.style.fontSize = '36px'; }, 150);
  clearTimeout(comboTimer);
  comboTimer = setTimeout(() => { el.style.opacity = '0'; el.style.transform = 'scale(0.8)'; comboCount = 0; }, 1500);
}

// ══════════════════════════════════════════
// 技能激活 VFX (能量聚集 + 技能名 + 全屏闪 + 震荡)
// ══════════════════════════════════════════
export function triggerSkillVFX(clsId, enable) {
  if(!enable) return;
  const cls = clsId || S.cls;
  const color = CLASS_COLORS[cls] || '#ffd700';
  const sk = S._lastSkillData;
  const isUlt = sk && sk.type === 'ultimate';
  
  // 能量聚集
  energyGather(cls);
  
  // 技能名大字
  if(sk) {
    const nameEl = document.createElement('div');
    nameEl.style.cssText = `position:fixed;top:45%;left:50%;transform:translate(-50%,-50%);z-index:300;pointer-events:none;font-size:${isUlt?32:22}px;font-weight:900;color:${color};text-shadow:0 0 20px ${color},0 0 60px ${color}88,0 4px 8px rgba(0,0,0,0.9);opacity:0;animation:skillNamePop 1.5s ease-out forwards;white-space:nowrap;font-family:sans-serif;letter-spacing:3px;`;
    nameEl.textContent = sk.name;
    document.body.appendChild(nameEl);
    setTimeout(() => nameEl.remove(), 1600);
  }
  
  // 全屏闪
  setTimeout(() => {
    const flash = document.createElement('div');
    flash.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(255,255,255,0.15);pointer-events:none;z-index:200;animation:flashHeavy 0.4s ease-out forwards;';
    document.body.appendChild(flash);
    setTimeout(() => flash.remove(), 450);
  }, 80);
  
  // 屏幕震荡 (奥义更强)
  setTimeout(() => screenShake(isUlt ? 8 : 5), 100);
}

// ══════════════════════════════════════════
// 受击反馈增强 (多层粒子 + 白模剪影 + 震荡)
// ══════════════════════════════════════════
export function animateMonsterHitEnhanced(dmg, isSkill, isCrit, clsId) {
  const el = document.getElementById('monsterWrap');
  if(!el) return;
  const cls = clsId || S.cls;
  const color = CLASS_COLORS[cls] || '#ffaa44';
  
  // 多层粒子爆发
  burstParticles(el, color, dmg > 20 ? 20 : 12, dmg > 30 ? 8 : 4);
  
  // 受击白模剪影 (重击)
  if(dmg > 15) {
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = 'hitSilhouette 0.3s ease-out forwards';
  }
  
  // 冲击波环
  const burst = document.getElementById('hitBurst');
  if(burst && dmg > 8) {
    burst.classList.remove('show','skill');
    void burst.offsetWidth;
    burst.classList.add('show', (isSkill||dmg>20)?'skill':'');
    if(dmg > 30) burst.style.setProperty('--is', '5');
    else if(dmg > 15) burst.style.setProperty('--is', '3.5');
  }
  
  // 暴击额外
  if(isCrit) {
    burstParticles(el, '#ffd700', 8, 6);
  }
  
  // 击退微移 (大伤害/技能)
  if((isSkill || dmg > 20) && el.querySelector('img')) {
    const img = el.querySelector('img');
    img.style.transition = 'none';
    img.style.transform = `translateX(${6+(isSkill?4:0)}px) translateY(-3px)`;
    setTimeout(() => { img.style.transition = 'transform 0.4s cubic-bezier(0.16,1,0.3,1)'; img.style.transform = ''; }, 80);
  }
  
  // 屏幕震动 (大伤害)
  if(dmg > 20) {
    screenShake(dmg > 35 ? 7 : 3);
  }
}

// ══════════════════════════════════════════
// 怪物死亡爆发
// ══════════════════════════════════════════
export function monsterDeathBurst() {
  const el = document.getElementById('monsterWrap');
  if(!el) return;
  burstParticles(el, '#ff8844', 30, 10);
  burstParticles(el, '#ffffff', 15, 8);
  
  const burst = document.createElement('div');
  burst.style.cssText = `position:absolute;top:50%;left:50%;width:80px;height:80px;margin:-40px 0 0 -40px;border-radius:50%;background:radial-gradient(circle,rgba(255,255,255,0.6) 0%,transparent 70%);pointer-events:none;z-index:50;animation:deathBurst 0.8s ease-out forwards;`;
  el.appendChild(burst);
  setTimeout(() => burst.remove(), 900);
  
  screenShake(8);
}

// ══════════════════════════════════════════
// 弹道轨迹增强 (残留粒子)
// ══════════════════════════════════════════
export function projectileTrail(cls) {
  const color = CLASS_COLORS[cls] || '#fff';
  const proj = document.getElementById('projectile');
  if(!proj) return;
  for(let i = 0; i < 5; i++) {
    const trail = document.createElement('div');
    trail.style.cssText = `position:absolute;right:${12+i*14}px;top:${i*3}px;width:${4-i*0.5}px;height:${4-i*0.5}px;background:${color};border-radius:50%;opacity:${0.6-i*0.1};pointer-events:none;z-index:19;animation:smokePuff 0.4s ease-out forwards;--smx:${-10-i*5}px;--smy:${(i-2)*8}px;`;
    proj.parentElement?.appendChild(trail);
    setTimeout(() => trail.remove(), 500);
  }
}

// ══════════════════════════════════════════
// 战斗 UI 能量脉冲 (奥义/大技能时)
// ══════════════════════════════════════════
export function combatUIPulse(cls) {
  const color = CLASS_COLORS[cls] || '#ffd700';
  const box = document.querySelector('.combat-box');
  if(!box) return;
  box.style.setProperty('--pc', color);
  box.style.animation = 'powerPulse 0.6s ease-in-out 2';
  setTimeout(() => box.style.animation = '', 1300);
}
